import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Alert,
  List,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItem,
  Tooltip
} from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  BugReport,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Schedule,
  TrendingUp,
  Search,
  Refresh,
  Visibility,
  Edit,
  Delete,
  Add,
  Lock,
  Webhook,
  CloudUpload,
  Payment,
  Sms as SmsIcon,
  Email as EmailIcon,
  Group,
  Notifications,
  Settings,
  Business,
  Wifi
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { 
  errorsService, 
  SystemError, 
  ErrorFilters, 
  ErrorStats, 
  ErrorAlertRule, 
  ErrorGroup 
} from '@services/errorsService';
import KPICard from '@shared/KPICard';
import StatusChip from '@shared/StatusChip';
import EmptyState from '@shared/EmptyState';
import SkeletonLoader from '@shared/SkeletonLoader';
import { formatForDisplay } from '@utils/dateFormatter';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`errors-tabpanel-${index}`}
      aria-labelledby={`errors-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ErrorsManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Erori
  const [errors, setErrors] = useState<SystemError[]>([]);
  const [filters, setFilters] = useState<ErrorFilters>({
    isResolved: false});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Setari
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [alertRules, setAlertRules] = useState<ErrorAlertRule[]>([]);
  const [errorGroups, setErrorGroups] = useState<ErrorGroup[]>([]);

  // Dialogs
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [alertRuleDialogOpen, setAlertRuleDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [_testDialogOpen, _setTestDialogOpen] = useState(false);
  const [editingError, setEditingError] = useState<SystemError | null>(null);
  const [editingAlertRule, setEditingAlertRule] = useState<ErrorAlertRule | null>(null);
  const [editingGroup, setEditingGroup] = useState<ErrorGroup | null>(null);

  // Incarcare erori
  const loadErrors = useCallback(async () => {
    setLoading(true);
    try {
      const data = await errorsService.getErrors({
        ...filters,
        page,
        limit: rowsPerPage
      });
      setErrors(data.errors || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error loading errors:', err);
      setError('Eroare la incarcarea erorilor');
      setErrors([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  // Incarcare statistici
  const loadStats = useCallback(async () => {
    try {
      const data = await errorsService.getErrorStats();
      setStats(data || null);
    } catch (err) {
      console.error('Error loading stats:', err);
      setStats(null);
    }
  }, []);

  // Incarcare reguli de alerta
  const loadAlertRules = useCallback(async () => {
    try {
      const data = await errorsService.getErrorAlertRules();
      setAlertRules(data.rules || []);
    } catch (err) {
      console.error('Error loading alert rules:', err);
      setAlertRules([]);
    }
  }, []);

  // Incarcare grupuri de erori
  const loadErrorGroups = useCallback(async () => {
    try {
      const data = await errorsService.getErrorGroups();
      setErrorGroups(data.groups || []);
    } catch (err) {
      console.error('Error loading error groups:', err);
      setErrorGroups([]);
    }
  }, []);

  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  useEffect(() => {
    if (activeTab === 1) {
      loadStats();
    } else if (activeTab === 2) {
      loadAlertRules();
    } else if (activeTab === 3) {
      loadErrorGroups();
    }
  }, [activeTab, loadStats, loadAlertRules, loadErrorGroups]);

  // Handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleFilterChange = (key: keyof ErrorFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleResolveError = async (id: string, resolution: string) => {
    try {
      await errorsService.resolveError(id, resolution);
      await loadErrors();
      setSuccess('Eroarea a fost rezolvata cu succes');
    } catch (err) {
      console.error('Error resolving error:', err);
      setError('Eroare la rezolvarea erorii');
    }
  };

  const handleAssignError = async (id: string, assignedTo: string) => {
    try {
      await errorsService.assignError(id, assignedTo);
      await loadErrors();
      setSuccess('Eroarea a fost atribuita cu succes');
    } catch (err) {
      console.error('Error assigning error:', err);
      setError('Eroare la atribuirea erorii');
    }
  };

  const handleDeleteError = async (id: string) => {
    try {
      await errorsService.deleteError(id);
      await loadErrors();
      setSuccess('Eroarea a fost stearsa cu succes');
    } catch (err) {
      console.error('Error deleting error:', err);
      setError('Eroare la stergerea erorii');
    }
  };

  const getErrorIcon = (type: string) => {
    switch (type) {
      case 'database': return <BugReport />;
      case 'api': return <Webhook />;
      case 'auth': return <Lock />;
      case 'payment': return <Payment />;
      case 'email': return <EmailIcon />;
      case 'sms': return <SmsIcon />;
      case 'file': return <CloudUpload />;
      case 'network': return <Wifi />;
      case 'validation': return <CheckCircle />;
      case 'business': return <Business />;
      case 'system': return <Settings />;
      default: return <BugReport />;
    }
  };

  const getErrorColor = (type: string) => {
    switch (type) {
      case 'database': return 'error';
      case 'api': return 'warning';
      case 'auth': return 'error';
      case 'payment': return 'error';
      case 'email': return 'info';
      case 'sms': return 'info';
      case 'file': return 'warning';
      case 'network': return 'warning';
      case 'validation': return 'info';
      case 'business': return 'warning';
      case 'system': return 'error';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      case 'debug': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Erori Sistem
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitorizare si gestionare erori din sistem
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadErrors}
            disabled={loading}
          >
            Reimprospateaza
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => _setTestDialogOpen(true)}
          >
            Test Eroare
          </Button>
        </Box>
      </Box>

      {/* Success/Error Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KPICard
              title="Total Erori"
              value={stats.total}
              icon={<BugReport />}
              color="primary"
              trend={stats.trends.length > 0 ? { value: stats.trends[stats.trends.length - 1].count, isPositive: true } : { value: 0, isPositive: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KPICard
              title="Nerezolvate"
              value={stats.unresolved}
              icon={<Warning />}
              color="warning"
              trend={{ value: stats.unresolved, isPositive: false }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KPICard
              title="Erori Critice"
              value={stats.bySeverity.critical + stats.bySeverity.high}
              icon={<ErrorIcon />}
              color="error"
              trend={{ value: stats.bySeverity.critical + stats.bySeverity.high, isPositive: false }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KPICard
              title="Timp Rezolvare"
              value={`${stats.resolutionTime.average.toFixed(1)}h`}
              icon={<Schedule />}
              color="info"
              trend={{ value: stats.resolutionTime.average, isPositive: true }}
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="errors tabs">
          <Tab label="Erori" icon={<BugReport />} iconPosition="start" />
          <Tab label="Statistici" icon={<TrendingUp />} iconPosition="start" />
          <Tab label="Reguli Alerta" icon={<Notifications />} iconPosition="start" />
          <Tab label="Grupuri" icon={<Group />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      )}

      {/* Erori Tab */}
      <TabPanel value={activeTab} index={0}>
        {/* Filtre */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Cauta"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tip</InputLabel>
                <Select
                  value={filters.type || ''}
                  label="Tip"
                  onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                >
                  <MenuItem value="">Toate</MenuItem>
                  <MenuItem value="database">Database</MenuItem>
                  <MenuItem value="api">API</MenuItem>
                  <MenuItem value="auth">Auth</MenuItem>
                  <MenuItem value="payment">Payment</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="file">File</MenuItem>
                  <MenuItem value="network">Network</MenuItem>
                  <MenuItem value="validation">Validation</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="system">System</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Severitate</InputLabel>
                <Select
                  value={filters.severity || ''}
                  label="Severitate"
                  onChange={(e) => handleFilterChange('severity', e.target.value || undefined)}
                >
                  <MenuItem value="">Toate</MenuItem>
                  <MenuItem value="low">Scazuta</MenuItem>
                  <MenuItem value="medium">Medie</MenuItem>
                  <MenuItem value="high">Ridicata</MenuItem>
                  <MenuItem value="critical">Critica</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.isResolved === undefined ? '' : filters.isResolved.toString()}
                  label="Status"
                  onChange={(e) => handleFilterChange('isResolved', e.target.value === '' ? undefined : e.target.value === 'true')}
                >
                  <MenuItem value="">Toate</MenuItem>
                  <MenuItem value="false">Nerezolvate</MenuItem>
                  <MenuItem value="true">Rezolvate</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Lista erori */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tip</TableCell>
                <TableCell>Titlu</TableCell>
                <TableCell>Mesaj</TableCell>
                <TableCell>Severitate</TableCell>
                <TableCell>Categorie</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errors && errors.length > 0 ? errors.map((error) => (
                <TableRow key={error.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getErrorIcon(error.type)}
                      <Chip
                        label={error.type}
                        color={getErrorColor(error.type) as any}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={error.isResolved ? 'normal' : 'bold'}>
                      {error.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {error.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={error.severity}
                      color={getSeverityColor(error.severity) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={error.category}
                      color={getCategoryColor(error.category) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatForDisplay(error.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={error.isResolved ? 'REZOLVAT' : 'NEREZOLVAT'}
                      color={error.isResolved ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Vezi detalii">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingError(error);
                            setErrorDialogOpen(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {!error.isResolved && (
                        <Tooltip title="Rezolva">
                          <IconButton
                            size="small"
                            onClick={() => handleResolveError(error.id, 'Rezolvata manual')}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Sterge">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteError(error.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nu exista erori de afisat
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TabPanel>

      {/* Statistici Tab */}
      <TabPanel value={activeTab} index={1}>
        {stats && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Erori pe tip
                  </Typography>
                  <List>
                    {Object.entries(stats.byType).map(([type, count]) => (
                      <ListItem key={type}>
                        <ListItemIcon>
                          {getErrorIcon(type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={type}
                          secondary={`${count} erori`}
                        />
                        <ListItemSecondaryAction>
                          <Typography variant="body2" color="text.secondary">
                            {((count / stats.total) * 100).toFixed(1)}%
                          </Typography>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Erori pe severitate
                  </Typography>
                  <List>
                    {Object.entries(stats.bySeverity).map(([severity, count]) => (
                      <ListItem key={severity}>
                        <ListItemIcon>
                          <Chip
                            label={severity}
                            color={getSeverityColor(severity) as any}
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={severity}
                          secondary={`${count} erori`}
                        />
                        <ListItemSecondaryAction>
                          <Typography variant="body2" color="text.secondary">
                            {((count / stats.total) * 100).toFixed(1)}%
                          </Typography>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Reguli Alerta Tab */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingAlertRule(null);
              setAlertRuleDialogOpen(true);
            }}
          >
            Adauga Regula
          </Button>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nume</TableCell>
                <TableCell>Descriere</TableCell>
                <TableCell>Conditii</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alertRules && alertRules.length > 0 ? alertRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.name}</TableCell>
                  <TableCell>{rule.description}</TableCell>
                  <TableCell>
                    <Chip label={rule.conditions.type} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={rule.isActive ? 'ACTIV' : 'INACTIV'}
                      color={rule.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingAlertRule(rule);
                        setAlertRuleDialogOpen(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton size="small">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nu exista reguli de alerta
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Grupuri Tab */}
      <TabPanel value={activeTab} index={3}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingGroup(null);
              setGroupDialogOpen(true);
            }}
          >
            Adauga Grup
          </Button>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nume</TableCell>
                <TableCell>Descriere</TableCell>
                <TableCell>Erori</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errorGroups && errorGroups.length > 0 ? errorGroups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell>{group.name}</TableCell>
                  <TableCell>{group.description}</TableCell>
                  <TableCell>
                    <Chip label={`${group.errors.length} erori`} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={group.isResolved ? 'REZOLVAT' : 'NEREZOLVAT'}
                      color={group.isResolved ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingGroup(group);
                        setGroupDialogOpen(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton size="small">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nu exista grupuri de erori
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>
    </Box>
  );
};

export default ErrorsManagement;
